import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AuthShell, GoogleButton } from "@/pages/authShared";
import { AlertCircle } from "lucide-react";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [existing, setExisting] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setExisting(false);
    const name = form.name.trim();
    const email = form.email.trim();
    if (!name) return setError("Veuillez indiquer votre nom.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return setError("Veuillez saisir une adresse e-mail valide.");
    if (form.password.length < 6) return setError("Le mot de passe doit contenir au moins 6 caractères.");
    setLoading(true);
    try {
      await register(name, email, form.password);
      toast.success("Compte créé avec succès");
      nav("/compte");
    } catch (err) {
      const msg = apiError(err);
      setError(msg);
      if (err?.response?.status === 400 && /existe déjà/i.test(msg)) setExisting(true);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Créer un compte" subtitle="Rejoignez Kit Parental."
      footer={<>Déjà inscrit ? <Link to="/connexion" className="text-terracotta hover:underline">Se connecter</Link></>}>
      <form onSubmit={submit} className="space-y-4" noValidate>
        {error && (
          <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 p-3 text-sm text-destructive" data-testid="register-error">
            <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
            <div>
              <p>{error}</p>
              {existing && (
                <p className="mt-1 text-ink-muted">
                  <Link to="/connexion" className="text-terracotta hover:underline">Se connecter</Link>
                  {" · "}
                  <Link to="/mot-de-passe-oublie" className="text-terracotta hover:underline">Mot de passe oublié ?</Link>
                </p>
              )}
            </div>
          </div>
        )}
        <div><Label htmlFor="name">Nom</Label><Input id="name" value={form.name} onChange={set("name")} className="bg-sand border-line mt-1" data-testid="register-name" /></div>
        <div><Label htmlFor="email">E-mail</Label><Input id="email" type="email" value={form.email} onChange={set("email")} className="bg-sand border-line mt-1" data-testid="register-email" /></div>
        <div>
          <Label htmlFor="password">Mot de passe</Label>
          <Input id="password" type="password" value={form.password} onChange={set("password")} className="bg-sand border-line mt-1" data-testid="register-password" />
          <p className="text-xs text-ink-muted mt-1">6 caractères minimum.</p>
        </div>
        <Button type="submit" disabled={loading} className="w-full bg-terracotta hover:bg-terracotta-dark text-white rounded-md" data-testid="register-submit">{loading ? "Création…" : "Créer mon compte"}</Button>
      </form>
      <div className="flex items-center gap-3 my-5"><div className="flex-1 h-px bg-line" /><span className="text-xs text-ink-muted">ou</span><div className="flex-1 h-px bg-line" /></div>
      <GoogleButton label="S'inscrire avec Google" />
    </AuthShell>
  );
}
